import { useState } from "react";
import { getUniLogoUrl, getUniInitials, getUniBrand } from "@/lib/uniLogos";

/**
 * UniLogo
 * -----------------------------------------------------------------------------
 * Circular university-logo tile. Resolves the best-available image URL via
 * `getUniLogoUrl` (Wikipedia-cached URL → Google favicon → null) and gracefully
 * falls back to a brand-coloured crest tile showing the uni's initials if the
 * network request 404s. Each institution has a hand-picked primary + accent
 * colour so the fallback still reads as "that university".
 *
 * Sizes:
 *   sm =  24px  · used in dense list rows
 *   md =  40px  · default
 *   lg =  56px  · card headers
 *   xl =  72px  · drilldown hero (`/uni/*`, `/degree/*`)
 *
 * Callers may pass:
 *   - `logoUrl`         — a Mongo-cached Wikipedia URL for premium quality
 *   - `officialDomain`  — an entry from `universities.official_domains`
 *   - `country`         — used to route CN/IN unis straight to initials
 */
export default function UniLogo({
  uniId,
  uniName,
  size = "md",
  className = "",
  logoUrl = null,
  officialDomain = null,
  country = null,
}) {
  const [failed, setFailed] = useState(false);
  const dim = size === "sm" ? 24 : size === "lg" ? 56 : size === "xl" ? 72 : 40;
  const url = getUniLogoUrl(uniId, {
    storedUrl: logoUrl,
    officialDomain,
    country,
  });
  const initials = getUniInitials(uniName);
  const brand = getUniBrand(uniId);

  // Circular tile — feels warmer, avoids competing with the square university
  // photography cards elsewhere on the page.
  const boxBase =
    "shrink-0 rounded-full border border-stone-200 flex items-center " +
    "justify-center overflow-hidden";

  if (!url || failed) {
    // Brand-coloured crest fallback.
    const fontSize =
      size === "sm" ? 10 : size === "xl" ? 22 : size === "lg" ? 18 : 13;
    return (
      <div
        data-testid={`uni-logo-fallback-${uniId || "unknown"}`}
        className={`${boxBase} font-editorial ${className}`}
        style={{
          width: dim, height: dim,
          background: brand.bg, color: brand.fg,
          fontSize, letterSpacing: "0.03em",
        }}
        title={uniName || ""}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      data-testid={`uni-logo-${uniId}`}
      className={`${boxBase} bg-white ${className}`}
      style={{ width: dim, height: dim }}
      title={uniName || ""}
    >
      <img
        src={url}
        alt={`${uniName} logo`}
        className="w-full h-full object-contain p-1.5"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
}
